import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  MoreHorizontal,
  FolderOpen,
  Calendar,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProjects, useDeleteProject, useUpdateProject } from '@/hooks/use-projects';
import { useToast } from '@/hooks/use-toast';
import { DeleteAlertDialog } from '@/components/admin/DeleteAlertDialog';
import type { Project } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';

export default function AdminProjetos() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: projects = [], isLoading, error } = useProjects();
  const deleteProject = useDeleteProject();
  const updateProject = useUpdateProject();
  const { toast } = useToast();

  const filteredProjects = projects.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.client.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const confirmDelete = (id: string, title: string) => {
    setDeleteId(id);
    setDeleteTitle(title);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteProject.mutateAsync(deleteId);
        toast({
          title: "Projeto excluído",
          description: `O projeto "${deleteTitle}" foi excluído com sucesso.`,
        });
        setDeleteDialogOpen(false);
      } catch (error) {
        toast({
          title: "Erro ao excluir",
          description: "Ocorreu um erro ao excluir o projeto.",
          variant: "destructive",
        });
      }
    }
  };

  const handleToggleVisibility = async (project: Project) => {
    try {
      const newVisible = !(project.visible !== false);
      await updateProject.mutateAsync({
        id: project.id,
        updates: { visible: newVisible }
      });
      toast({
        title: newVisible ? "Projeto visível" : "Projeto ocultado",
        description: `O projeto "${project.title}" ${newVisible ? 'agora está visível' : 'foi ocultado'} no site.`,
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível alterar a visibilidade do projeto.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-center py-20">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <FolderOpen className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-red-900">Erro ao carregar projetos</h2>
        <p className="text-red-700">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projetos</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie e organize seu portfólio criativo
          </p>
        </div>
        <Button asChild className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
          <Link to="/admin/projetos/novo">
            <Plus className="mr-2 h-4 w-4" />
            Novo Projeto
          </Link>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 shadow-lg overflow-hidden backdrop-blur-sm bg-card/50">
          <CardHeader className="border-b bg-muted/20 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              {/* Search Bar */}
              <div className="relative flex-1 max-w-md group">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Buscar por título ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-muted group-focus-within:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>{filteredProjects.length} resultados</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[450px] pl-6">Projeto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Ano</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {filteredProjects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center h-40 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-3">
                            <FolderOpen className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                          <p className="font-medium">Nenhum projeto encontrado</p>
                          <p className="text-sm text-muted-foreground/70">
                            {searchTerm ? 'Tente buscar com outros termos.' : 'Comece adicionando seu primeiro projeto.'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProjects.map((project, index) => (
                      <motion.tr
                        key={project.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: index * 0.05 }}
                        className="group hover:bg-muted/40 transition-colors border-b border-border/50 last:border-0"
                        style={{ display: 'table-row' }} // Essential for table layout with motion
                        layoutId={project.id}
                      >
                        <TableCell className="pl-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="h-14 w-20 rounded-lg bg-muted flex items-center justify-center overflow-hidden ring-1 ring-border shadow-sm group-hover:shadow-md transition-all shrink-0">
                              {project.coverImage ? (
                                <img
                                  src={project.coverImage}
                                  alt={project.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <FolderOpen className="h-6 w-6 text-muted-foreground/40" />
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold text-foreground group-hover:text-blue-600 transition-colors">{project.title}</span>
                              {project.featured && (
                                <Badge variant="secondary" className="w-fit text-[10px] h-4 px-1.5 bg-yellow-500/10 text-yellow-600 border-yellow-200">
                                  Destaque
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-medium">{project.client || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground bg-muted/30 px-2 py-1 rounded w-fit text-xs">
                            <Calendar className="h-3 w-3" />
                            {project.year}
                          </div>
                        </TableCell>
                        <TableCell>
                          {project.visible !== false ? (
                            <div className="flex items-center gap-2">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                              </span>
                              <span className="text-sm font-medium text-green-600">Visível</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="relative flex h-2 w-2">
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
                              </span>
                              <span className="text-sm font-medium text-slate-500">Oculto</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-background">
                                  <span className="sr-only">Abrir menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[180px]">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuItem asChild className="cursor-pointer">
                                  <Link to={`/admin/projetos/${project.id}/editar`}>
                                    <Edit className="mr-2 h-4 w-4 text-blue-500" />
                                    Editar
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className="cursor-pointer">
                                  <Link to={`/projetos/${project.id}`} target="_blank">
                                    <ExternalLink className="mr-2 h-4 w-4 text-purple-500" />
                                    Ver no Site
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleVisibility(project)} className="cursor-pointer">
                                  {project.visible !== false ? (
                                    <>
                                      <EyeOff className="mr-2 h-4 w-4 text-slate-500" />
                                      Ocultar
                                    </>
                                  ) : (
                                    <>
                                      <Eye className="mr-2 h-4 w-4 text-green-500" />
                                      Publicar
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => confirmDelete(project.id, project.title)}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      <DeleteAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Excluir "${deleteTitle}"?`}
        description="Esta ação não pode ser desfeita. Isso excluirá permanentemente o projeto e todos os seus dados associados."
        onConfirm={handleDelete}
        isDeleting={deleteProject.isPending}
      />
    </div>
  );
}
